import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';

interface TransformationPreviewProps {
  viewId: string;
  columnName: string;
  transformation: string;
}

interface PreviewData {
  original: any[];
  transformed: any[];
  error?: string;
}

export const TransformationPreview: React.FC<TransformationPreviewProps> = ({
  viewId,
  columnName,
  transformation,
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PreviewData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPreview = async () => {
      setLoading(true);
      setError(null);

      try {
        // TODO: Replace with actual API call when backend endpoint is ready
        // For now, this is a placeholder implementation

        // const response = await fetch(`/api/views/${viewId}/preview-transformation`, {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({
        //     column_name: columnName,
        //     transformation: transformation,
        //     limit: 10
        //   })
        // });

        // if (!response.ok) {
        //   throw new Error('Failed to fetch preview');
        // }

        // const result = await response.json();
        // setData(result);

        // Placeholder: Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));

        // Placeholder data for demonstration
        setData({
          original: [
            'sample value 1',
            'sample value 2',
            'sample value 3',
            null,
            'sample value 5',
          ],
          transformed: [
            'TRANSFORMED 1',
            'TRANSFORMED 2',
            'TRANSFORMED 3',
            null,
            'TRANSFORMED 5',
          ],
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    };

    if (viewId && columnName && transformation) {
      fetchPreview();
    }
  }, [viewId, columnName, transformation]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading preview...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-success" />
          Transformation Preview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Alert className="mb-4">
          <AlertDescription className="text-xs">
            Preview shows the first 10 rows. The transformation will be applied to all rows in the view.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-2 gap-4">
          {/* Original Values */}
          <div>
            <div className="text-sm font-medium mb-2">Original ({columnName})</div>
            <div className="space-y-1">
              {data.original.map((value, index) => (
                <div
                  key={index}
                  className="text-sm p-2 bg-muted rounded border"
                >
                  {value === null ? (
                    <span className="text-muted-foreground italic">NULL</span>
                  ) : (
                    <span className="font-mono">{String(value)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Transformed Values */}
          <div>
            <div className="text-sm font-medium mb-2">Transformed</div>
            <div className="space-y-1">
              {data.transformed.map((value, index) => (
                <div
                  key={index}
                  className="text-sm p-2 bg-success/10 rounded border border-success"
                >
                  {value === null ? (
                    <span className="text-muted-foreground italic">NULL</span>
                  ) : (
                    <span className="font-mono">{String(value)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {data.error && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Transformation Error:</strong> {data.error}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
